import { AlterableBusinessHeader, BusinessExcelInfo } from '@/coreDomainsModule/entities/businessExcelInfo';

/**
 * 取引情報紐付け
 * @param useLinkingType - どのような紐付けを行うか
 */
export interface LinkingBusiness {
  useLinkingType: UseLinkingType;
  previousExcelInfo?: BusinessExcelInfo;
  linkedBusinessHeaders: LinkingBusinessHeader[];
}

/**
 * 紐付けの種類
 */
type UseLinkingType = 'Default' | 'Original';

/**
 * エラーの種類
 */
type ValidateError = 'NotSelected' | 'None';

/**
 * 紐付けのヘッダー
 * @param displayName - 表示名
 * @param key - キー
 * @param value - 選択された値を保持する
 * @param required - 入力必須かどうか
 * @param unused - 登録しないヘッダーかどうか(undefinedの場合は登録する/しないは調整できない)
 * @param validateError - エラーの種類
 */
export interface LinkingBusinessHeader {
  displayName: string;
  key: AlterableBusinessHeader;
  value?: string;
  required: boolean;
  unused?: boolean;
  validateError: ValidateError;
}

export const LINKING_BUSINESS_HEADERS: LinkingBusinessHeader[] = [
  { displayName: '集計', key: 'isAggregateBusiness', value: undefined, required: true, unused: undefined, validateError: 'None' },
  { displayName: '置場コード', key: 'warehouseNumber', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: '置場名', key: 'warehouse', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: '仕入先コード', key: 'supplierNumber', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: '仕入先名', key: 'supplierName', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: '商品コード', key: 'businessNumber', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: '商品名', key: 'businessName', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: '単位', key: 'unit', value: undefined, required: false, unused: undefined, validateError: 'None' },
  { displayName: '容量', key: 'package', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: '売上先コード', key: 'customerNumber', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: '売上先名', key: 'customerName', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: '送り先コード', key: 'salesDestinationNumber', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: '送り先名', key: 'salesDestinationName', value: undefined, required: true, unused: false, validateError: 'None' },
  { displayName: 'リードタイム', key: 'leadTime', value: undefined, required: true, unused: undefined, validateError: 'None' },
  { displayName: 'リードタイム単位', key: 'leadTimeUnit', value: undefined, required: true, unused: undefined, validateError: 'None' },
  { displayName: '国内輸送日数', key: 'inTransitDays', value: undefined, required: false, unused: undefined, validateError: 'None' },
  {
    displayName: '標準在庫指数（下限）',
    key: 'standardInventoryIndexLower',
    value: undefined,
    required: true,
    unused: undefined,
    validateError: 'None',
  }, // ここだけ、キー情報がIBusinessと1対1対応していないので注意
  {
    displayName: '標準在庫指数（上限）',
    key: 'standardInventoryIndexUpper',
    value: undefined,
    required: true,
    unused: undefined,
    validateError: 'None',
  }, // ここだけ、キー情報がIBusinessと1対1対応していないので注意
  {
    displayName: '標準在庫指数単位',
    key: 'standardInventoryIndexUnit',
    value: undefined,
    required: true,
    unused: undefined,
    validateError: 'None',
  }, // ここだけ、キー情報がIBusinessと1対1対応していないので注意
  { displayName: '最小発注数量（MOQ）', key: 'MOQ', value: undefined, required: false, unused: undefined, validateError: 'None' },
  { displayName: '発注単位', key: 'orderPackage', value: undefined, required: false, unused: undefined, validateError: 'None' },
  { displayName: '製造社', key: 'manufacturer', value: undefined, required: false, unused: undefined, validateError: 'None' },
  { displayName: 'エンドユーザー', key: 'endUser', value: undefined, required: false, unused: undefined, validateError: 'None' },
  {
    displayName: '仕入担当者',
    key: 'aggregateBusinessResponsibleUsers',
    value: undefined,
    required: false,
    unused: undefined,
    validateError: 'None',
  },
  {
    displayName: '販売担当者',
    key: 'baseBusinessResponsibleUsers',
    value: undefined,
    required: false,
    unused: undefined,
    validateError: 'None',
  },
  {
    displayName: '販売単価',
    key: 'salesUnitPrice',
    value: undefined,
    required: false,
    unused: undefined,
    validateError: 'None',
  },
  {
    displayName: '仕入単価',
    key: 'purchaseUnitPrice',
    value: undefined,
    required: false,
    unused: undefined,
    validateError: 'None',
  },
];

/**
 * 紐付け情報（読み替え用）
 * @param value - 読み替え用のヘッダー名
 * @param unused - 未使用かどうか
 */
export interface TransformLinkingInput {
  value: string;
  unused: boolean;
}
